import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/shop';
import { usePageTitle } from '../a11y/usePageTitle';
import { brand } from '../brand/brand';
import { ApiRequestError } from '../api/client';

/**
 * "Deals by text": the SMS marketing opt-in form. SOW 2.6, and the page a 10DLC
 * campaign registration points at as the opt-in flow.
 *
 * THE DISCLOSURE IS THE CONTRACT. The text beside the checkbox is what the person
 * agrees to, and it is sent to the server word for word with the number, so the
 * stored consent proves exactly what was shown. Change the wording here and the
 * next row records the new wording; nothing is edited after the fact.
 *
 * What the carriers look for on this page, and why each line is here:
 *   - who is sending (the brand name), and what (the program, in plain words)
 *   - that consent is not a condition of purchase
 *   - message frequency, "Msg & data rates may apply", STOP and HELP
 *   - links to the privacy policy and the terms
 *   - an unticked checkbox: consent is an action, never a default
 */

export const SMS_CONSENT_TEXT =
  `By checking this box and tapping "Text me deals", I agree to receive recurring automated marketing text messages from ${brand.name} at the number I provided, including the deal of the week and reminders about credit left on my vouchers. Consent is not a condition of any purchase. Up to 4 messages a month. Message and data rates may apply. Reply STOP to cancel at any time, HELP for help.`;

export function SmsOptIn() {
  usePageTitle('Deals by text');

  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);
  const [state, setState] = useState<'ready' | 'working' | 'done'>('ready');
  const [error, setError] = useState<string | null>(null);

  const ready = consent && phone.replace(/\D/g, '').length >= 10 && state === 'ready';

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!ready) return;

    setState('working');
    setError(null);

    try {
      await api.smsOptIn({
        phone,
        consent,
        consentText: SMS_CONSENT_TEXT,
        pageUrl: window.location.href,
      });
      setState('done');
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Something went wrong. Please try again.');
      setState('ready');
    }
  }

  return (
    <article className="doc">
      <header className="stack" style={{ gap: 6 }}>
        <p className="faint" style={{ margin: 0 }}>{brand.name} Text Alerts</p>
        <h1>Get the deal of the week by text</h1>
        <p className="lead" style={{ margin: 0 }}>
          One text a week with a real offer at a local business, like $50 of credit for
          $40. Opt out any time by replying STOP.
        </p>
      </header>

      <div className="stack" style={{ gap: 22 }}>
        {state === 'done' ? (
          <div className="panel stack" style={{ gap: 10 }} role="status">
            <h2>You are in</h2>
            <p className="muted">
              We will text {brand.name} deals to {phone}. Your first message confirms it;
              reply STOP at any time to cancel, or HELP for help.
            </p>
            <Link to="/" className="btn secondary" style={{ alignSelf: 'flex-start' }}>Browse deals</Link>
          </div>
        ) : (
          <form className="panel stack" style={{ gap: 14, maxWidth: 560 }} onSubmit={(e) => void submit(e)} noValidate>
            <div className="field">
              <label htmlFor="sms-phone">Your mobile number</label>
              <input
                id="sms-phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="(201) 555-0123"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={state === 'working'}
                aria-describedby="sms-phone-hint"
              />
              <span className="hint" id="sms-phone-hint">US mobile numbers only.</span>
            </div>

            <label className="check" style={{ alignItems: 'flex-start', gap: 10 }}>
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                disabled={state === 'working'}
                style={{ marginTop: 4 }}
              />
              <span className="muted">{SMS_CONSENT_TEXT}</span>
            </label>

            <p className="faint" style={{ margin: 0 }}>
              See our <Link to="/privacy">Privacy Policy</Link> and <Link to="/terms">Terms</Link>.
              Mobile information will not be shared with or sold to third parties or
              affiliates for marketing or promotional purposes.
            </p>

            {error !== null && (
              <p className="notice error" role="alert">{error}</p>
            )}

            <div>
              <button type="submit" className="btn cta" disabled={!ready}>
                {state === 'working' ? 'Saving…' : 'Text me deals'}
              </button>
            </div>
          </form>
        )}

        <section className="stack" style={{ gap: 8 }}>
          <h2>What you get, and how to stop</h2>
          <p className="muted">
            Marketing texts from {brand.name}: the deal of the week at local businesses
            near you, and a reminder when you have credit left on a voucher. Up to 4
            messages a month. Message and data rates may apply. Consent is not a condition
            of any purchase.
          </p>
          <p className="muted">
            Reply <strong>STOP</strong> to any message to cancel; you will get one text
            confirming you are out, and nothing after that. Reply <strong>HELP</strong> for
            help, or email <a href={`mailto:${brand.supportEmail}`}>{brand.supportEmail}</a>.
          </p>
          <p className="muted">
            Sign-in codes and voucher confirmations are separate: they are sent when you
            ask for them, whether or not you are in this program.
          </p>
        </section>
      </div>

      <nav aria-label="Related" className="doc-nav">
        <Link to="/terms">Terms</Link>
        <Link to="/privacy">Privacy</Link>
        <Link to="/support">Support</Link>
        <Link to="/faq">FAQ</Link>
      </nav>
    </article>
  );
}
